import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import type { ServiceDTO } from '@/features/services/api';
import { useShiftMutations } from '@/features/shifts/hooks';
import { scheduleShiftSchema, type ScheduleShiftInput } from '@/features/shifts/schemas';

interface ScheduleShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateISO: string | null;
  services: ServiceDTO[];
  cnpjId: string | null;
  onScheduled?: () => void;
}

export function ScheduleShiftDialog({
  open,
  onOpenChange,
  dateISO,
  services,
  cnpjId,
  onScheduled,
}: ScheduleShiftDialogProps) {
  const { show } = useToast();
  const { create } = useShiftMutations(cnpjId);

  const form = useForm<ScheduleShiftInput>({
    resolver: zodResolver(scheduleShiftSchema),
    defaultValues: {
      serviceId: services[0]?.id ?? '',
      date: dateISO ?? new Date().toISOString().slice(0, 10),
      overrideFixedValue: null,
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        serviceId: services[0]?.id ?? '',
        date: dateISO ?? new Date().toISOString().slice(0, 10),
        overrideFixedValue: null,
        notes: '',
      });
    }
  }, [open, dateISO, services, form]);

  const onSubmit = form.handleSubmit((values) => {
    create.mutate(
      {
        serviceId: values.serviceId,
        date: values.date,
        overrideFixedValue:
          values.overrideFixedValue === null || values.overrideFixedValue === undefined
            ? null
            : Number(values.overrideFixedValue),
        notes: values.notes || null,
      },
      {
        onSuccess: () => {
          show({ title: 'Plantão agendado', variant: 'success' });
          onOpenChange(false);
          onScheduled?.();
        },
        onError: (e) =>
          show({
            title: 'Erro ao agendar',
            description: e instanceof Error ? e.message : '',
            variant: 'error',
          }),
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agendar plantão</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <DialogBody className="flex flex-col gap-4">
            <FormField
              id="date"
              label="Data"
              required
              error={form.formState.errors.date?.message}
            >
              <Input id="date" type="date" {...form.register('date')} />
            </FormField>

            <FormField
              id="serviceId"
              label="Serviço"
              required
              error={form.formState.errors.serviceId?.message}
            >
              <select
                id="serviceId"
                {...form.register('serviceId')}
                className="flex h-11 w-full rounded-md border border-(--color-border) bg-(--color-surface) px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
              >
                {services.length === 0 && <option value="">Nenhum serviço cadastrado</option>}
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField
              id="overrideFixedValue"
              label="Valor fixo (opcional)"
              hint="Deixe em branco para usar o valor padrão do serviço."
              error={form.formState.errors.overrideFixedValue?.message}
            >
              <Input
                id="overrideFixedValue"
                type="number"
                min={0}
                step="0.01"
                {...form.register('overrideFixedValue', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
            </FormField>

            <FormField id="notes" label="Observações" error={form.formState.errors.notes?.message}>
              <Input id="notes" type="text" {...form.register('notes')} />
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={create.isPending || services.length === 0}>
              {create.isPending ? 'Agendando…' : 'Agendar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
